"use client";

import { adminCreateArtifact, adminCreateTag, adminDeleteArtifacts, adminDeleteTags, adminGetTags, adminUpdateTag, chatUpdateArtifact, chatUpdateTag, getToolUse } from "@/api/sdk.gen";
import { usePathParam, useLocalNumberList } from "@/components/path-params";
import { ForwardRefEditor } from "@/components/richtext/ForwardRefEditor";
import { ActionHandler, EntryItem, EntryList, useRefState } from "@/components/UI/tools";
import { citationsPlugin } from "@/components/richtext/Citation";
import { storeCitedText } from "@/components/richtext/Citation";
import {
    addActivePlugin$,
    addComposerChild$,
    codeBlockPlugin,
    codeMirrorPlugin,
    headingsPlugin,
    listsPlugin,
    markdownShortcutPlugin,
    MDXEditorMethods,
    quotePlugin,
    realmPlugin,
    rootEditor$,
    tablePlugin,
    thematicBreakPlugin,
    toolbarPlugin,
    useCellValue,
} from "@mdxeditor/editor";

import { COMMAND_PRIORITY_HIGH, COMMAND_PRIORITY_LOW, DROP_COMMAND, KEY_ENTER_COMMAND, PASTE_COMMAND, $getRoot, $getSelection, $isRangeSelection, type LexicalEditor } from "lexical";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppState, appState } from "../../../appState";
import { observer } from "mobx-react-lite";
import { chatState } from "@/app/chatState";
import { runsState } from "@/app/runsState";
import { ArrowDown, Edit, Replay } from "@/components/UI/svgs";
import { ChatMessage, ChatMessageBlockText } from "../../../typing/chat";
import { MessageContent } from "./chatMessage";
import { buildCitationRegistry } from "./citationLabels";
import { useLocation } from "react-router-dom";
import { client } from "@/api/client.gen";
import { applyCreatedDocumentMessage } from "../applyCreatedArtifact";
import {
    ComposerChip,
    displayNameFromFilename,
    documentContentType,
    formatByteSize,
    isAllowedDocumentFile,
    labelFromPastedText,
    MAX_FILE_BYTES,
    nextChipId,
    shouldArmPastedText,
} from "../composerAttachments";
import { chatUploadDocument } from "@/apiExtra/documentFiles";

/**
 * Super-admin only: copy this conversation as a Chat Evaluation context snapshot
 * ({tag_ids, messages}), ready to paste into the Contexts tab.
 */
const CopySnapshotButton = observer(() => {
    const { pathname } = useLocation();
    const [copied, setCopied] = useState(false);

    if (!appState.user?.is_super_admin) return null;

    const tagIds = pathname
        .replace(/^\/workspace\/?/, "")
        .replace("chat", "")
        .replace(/^-/, "")
        .split("-")
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));

    const copy = async () => {
        try {
            const response = await client.post({
                url: "/api/chat/export-conversation-snapshot",
                body: { tag_ids: tagIds },
                headers: { "Content-Type": "application/json" },
            });
            if (response.error) throw new Error(appState.t("chat.exportFailed"));
            await navigator.clipboard.writeText(JSON.stringify(response.data));
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            appState.setModal({
                title: appState.t("common.error"),
                body: e instanceof Error ? e.message : appState.t("chat.copySnapshotFailed"),
            });
        }
    };

    return (
        <button
            title={appState.t("chat.copySnapshot")}
            className="ghost-action ghost-action-mono"
            onClick={copy}
        >
            {copied ? "copied" : "copy"}
        </button>
    );
});


function longestCommonPrefix(values: string[]): string {
    if (values.length === 0) return "";
    const lowers = values.map((value) => value.toLowerCase());
    let prefix = lowers[0];
    for (const value of lowers.slice(1)) {
        let i = 0;
        while (i < prefix.length && i < value.length && prefix[i] === value[i]) i++;
        prefix = prefix.slice(0, i);
        if (!prefix) break;
    }
    return values[0].slice(0, prefix.length);
}

const SLASH_TOKEN_RE = /^([\p{L}\p{N}_]*)/u;

function composerPlainText(md: string): string {
    return md.replace(/\\([_*])/g, "$1");
}

/** Slash query while the token is only letters/numbers/underscore; otherwise closed. */
function parseSlashQuery(md: string): string | null {
    const trimmed = composerPlainText(md).trim();
    if (!trimmed.startsWith("/")) return null;
    const rest = trimmed.slice(1);
    const token = rest.match(SLASH_TOKEN_RE)?.[1] ?? "";
    if (rest.length > token.length) return null;
    return token;
}

function parseSlashSend(md: string): { token: string; rest: string } | null {
    const trimmed = composerPlainText(md).trim();
    if (!trimmed.startsWith("/")) return null;
    const rest = trimmed.slice(1);
    const token = rest.match(SLASH_TOKEN_RE)?.[1] ?? "";
    return { token, rest: rest.slice(token.length).trim() };
}


interface ChatProps {
    onSendMessage: (message: string, is_injection?: boolean, replay_message_id?: number, skill_key?: string) => Promise<void>;
    onCancel: () => void;
    scrollRef?: React.RefObject<HTMLDivElement | null>;
    /** Set on mobile, where the messages scroll on their own instead of the whole row. */
    messagesScrollRef?: React.RefObject<HTMLDivElement | null>;
    scrollRefIsAtBottom?: boolean;
    onComposerFocus?: () => void;
}

type ChatRunState = typeof chatState.state;

const statusLabel = (state: ChatRunState): string => {
    switch (state) {
        case "awaiting_backend":
            return appState.t("chat.sending");
        case "awaiting_llm":
            return appState.t("chat.awaitingAgent");
        case "streaming":
            return appState.t("chat.streaming");
        case "awaiting_event_summarisation":
            return appState.t("chat.awaitingSummarisation");
        case "awaiting_document_summarisation":
            return appState.t("chat.awaitingDocumentSummarisation");
        case "conversation_locked":
            return appState.t("chat.conversationLocked");
        default:
            return "";
    }
};

/** Last row of a running conversation: what the agent is doing, plus anything queued for it. */
const StatusIndicator = observer(({ cb }: { cb?: () => void }) => {
    const state = chatState.state;
    useEffect(() => {
        const handleEvent = (e: KeyboardEvent) => {
            if (e.key === "Escape" && cb) {
                e.preventDefault();
                cb();
            }
        };
        document.addEventListener("keydown", handleEvent);
        return () => {
            document.removeEventListener("keydown", handleEvent);
        };
    }, [cb]);

    return (
        <div className="thread-status-wrap">
            {chatState.queuedSteers.map((steer) => (
                <div key={steer.id} className="thread-queued">
                    <small className="thread-queued-label">{appState.t("chat.queued")}</small>
                    <p className="thread-queued-text">{steer.text}</p>
                </div>
            ))}
            <div className="thread-status">
                <div
                    className="loader w-1 h-1"
                    onClick={() => cb?.()}
                    title={appState.t("chat.stop")}
                />
                <small className="thread-status-label">{statusLabel(state === "idle" ? "awaiting_llm" : state)}</small>
            </div>
        </div>
    );
});


const EnterHandlerPlugin = realmPlugin<{ lexicalEditorRef: React.MutableRefObject<LexicalEditor | null> }>({
    init(realm, params) {
        if (!params) return;
        const lexicalEditorRef = params.lexicalEditorRef;
        realm.pubIn({
            [addActivePlugin$]: "enterHandler",
            [addComposerChild$]: () => {
                const editor = useCellValue(rootEditor$);

                useEffect(() => {
                    lexicalEditorRef.current = editor ?? null;
                    return () => {
                        if (lexicalEditorRef.current === editor) {
                            lexicalEditorRef.current = null;
                        }
                    };
                }, [editor]);

                useEffect(() => {
                    if (!editor) return;

                    editor.registerCommand<KeyboardEvent>(
                        KEY_ENTER_COMMAND,
                        (event: KeyboardEvent) => {
                            if (event.key === "Enter" && !event.shiftKey) {
                                event.preventDefault();
                                return true;
                            }
                            return false;
                        },
                        COMMAND_PRIORITY_LOW
                    );
                }, [editor]);
            },
            //[addLexicalNode$]: ArtifactNode,
        });
    },
    update(realm, params) { },
});

type PasteHandlerParams = {
    onArmText: React.MutableRefObject<(text: string) => void>;
    onFiles: React.MutableRefObject<(files: File[]) => void>;
};

const PasteHandlerPlugin = realmPlugin<PasteHandlerParams>({
    init(realm, params) {
        if (!params) return;
        const handlers = params;
        realm.pubIn({
            [addActivePlugin$]: "pasteHandler",
            [addComposerChild$]: () => {
                const editor = useCellValue(rootEditor$);

                useEffect(() => {
                    if (!editor) return;

                    const armFromClipboard = (data: DataTransfer | null) => {
                        if (!data) return false;
                        const files = Array.from(data.files || []);
                        if (files.length > 0) {
                            handlers.onFiles.current(files);
                            return true;
                        }
                        const text = data.getData("text/plain");
                        if (text && shouldArmPastedText(text)) {
                            handlers.onArmText.current(text);
                            return true;
                        }
                        return false;
                    };

                    const unpaste = editor.registerCommand(
                        PASTE_COMMAND,
                        (event: ClipboardEvent) => {
                            if (armFromClipboard(event.clipboardData)) {
                                event.preventDefault();
                                return true;
                            }
                            return false;
                        },
                        COMMAND_PRIORITY_HIGH
                    );
                    const undrop = editor.registerCommand(
                        DROP_COMMAND,
                        (event: DragEvent) => {
                            if (armFromClipboard(event.dataTransfer)) {
                                event.preventDefault();
                                return true;
                            }
                            return false;
                        },
                        COMMAND_PRIORITY_HIGH
                    );
                    return () => {
                        unpaste();
                        undrop();
                    };
                }, [editor]);
            },
        });
    },
    update() { },
});


export const Chat = observer(({ onSendMessage, onCancel, scrollRef, messagesScrollRef, scrollRefIsAtBottom, onComposerFocus }: ChatProps) => {
    const running = runsState.isRunning(chatState.conversationId);
    const runningRef = useRef(running);
    runningRef.current = running;


    const inputEditor = useRef<MDXEditorMethods>(null);
    const lexicalEditorRef = useRef<LexicalEditor | null>(null);
    const composerRef = useRef<HTMLDivElement>(null);
    const chatRootRef = useRef<HTMLDivElement>(null);
    const dockRef = useRef<HTMLDivElement>(null);
    const skipSlashIndexResetRef = useRef(false);
    const suppressSlashOpenRef = useRef(false);
    const isAtBottomRef = useRef<boolean>(false);
    const [skills, setSkills] = useState<{ key: string; command: string }[]>([]);
    const [slashQuery, setSlashQuery] = useState<string | null>(null);
    const [slashIndex, setSlashIndex] = useState(0);
    const [chips, setChips] = useState<ComposerChip[]>([]);
    const chipsRef = useRef<ComposerChip[]>([]);
    chipsRef.current = chips;
    const sendingRef = useRef(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const onArmTextRef = useRef<(text: string) => void>(() => {});
    const onFilesRef = useRef<(files: File[]) => void>(() => {});
    // Resolves when every upload started so far has landed. A send that arrives
    // mid-upload waits on this instead of being dropped.
    const uploadsRef = useRef<Promise<void> | null>(null);
    const [sendQueued, setSendQueued] = useState(false);

    useEffect(() => {
        let cancelled = false;
        client.post({
            url: "/api/chat/list-skills",
            body: {},
            headers: { "Content-Type": "application/json" },
        }).then((response) => {
            if (cancelled) return;
            const data = response.data as { skills?: { key: string; command: string }[] } | undefined;
            if (data?.skills) setSkills(data.skills);
        }).catch(() => undefined);
        return () => { cancelled = true; };
    }, [appState.locale]);

    const slashMatches = useMemo(() => {
        if (slashQuery == null) return [];
        const q = slashQuery.toLowerCase();
        return skills.filter((s) => s.command.toLowerCase().startsWith(q) || s.command.toLowerCase().includes(q));
    }, [skills, slashQuery]);

    const persistDocumentChips = () => {
        const fileChips = chipsRef.current.filter((c) => c.documentId > 0);
        for (const chip of fileChips) {
            chatState.chatObjects.update([{
                key: { type: "document", id: chip.documentId },
                value: { isSelected: true },
            }]);
            chatState.setSelectedObjectKey({ type: "document", id: chip.documentId }, false);
        }
        return true;
    };

    // On touch devices sending closes the keyboard; on desktop focus stays for the next message.
    const blurComposerOnTouch = () => {
        if (!window.matchMedia("(pointer: coarse)").matches) return;
        const active = document.activeElement;
        if (active instanceof HTMLElement && active.closest(".composer")) active.blur();
    };

    const invokeSkill = async (key: string, extra = "") => {
        blurComposerOnTouch();
        setSlashQuery(null);
        inputEditor.current?.setMarkdown("");
        const armed = persistDocumentChips();
        if (!armed) return;
        setChips([]);
        onSendMessage(extra, false, undefined, key);
    };

    // A document is owned by one tag, so uploading needs an unambiguous scope.
    const uploadDisabled = chatState.tagIds.length !== 1;

    const uploadFiles = useCallback(async (files: File[]) => {
        // A document belongs to exactly one tag, so there is no sound way to pick
        // an owner when the chat spans several. The paperclip is disabled in that
        // case; this guard covers drag-and-drop and paste, which bypass the button.
        if (chatState.tagIds.length !== 1) {
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("chat.uploadNeedsSingleTag"),
            });
            return;
        }
        for (const file of files) {
            if (!isAllowedDocumentFile(file)) {
                appState.setModal({
                    title: appState.t("common.error"),
                    body: appState.t("chat.fileTypeNotAllowed"),
                });
                continue;
            }
            if (file.size > MAX_FILE_BYTES) {
                appState.setModal({
                    title: appState.t("common.error"),
                    body: appState.t("chat.fileTooLarge"),
                });
                continue;
            }
            const contentType = documentContentType(file);
            const label = displayNameFromFilename(file.name, file.name);
            const chipId = nextChipId();
            setChips((prev) => [
                ...prev,
                {
                    id: chipId,
                    kind: "file",
                    label,
                    sizeHint: formatByteSize(file.size),
                    documentId: -1,
                    contentType,
                    uploading: true,
                },
            ]);
            const res = await chatUploadDocument({
                file,
                tag_ids: chatState.tagIds,
            });
            if (res.error || !res.data) {
                setChips((prev) => prev.filter((c) => c.id !== chipId));
                appState.setModal({
                    title: appState.t("common.error"),
                    body: typeof res.error?.detail === "string"
                        ? res.error.detail
                        : appState.t("chat.uploadFailed"),
                });
                continue;
            }
            const objectId = applyCreatedDocumentMessage(res.data as ChatMessage, label);
            if (objectId == null) {
                setChips((prev) => prev.filter((c) => c.id !== chipId));
                continue;
            }
            setChips((prev) => prev.map((c) =>
                c.id === chipId
                    ? { ...c, documentId: objectId, uploading: false, kind: "file", label, sizeHint: formatByteSize(file.size), contentType }
                    : c
            ));
        }
    }, []);

    // Chain uploads so a single await covers every batch, in the order started.
    const startUpload = useCallback((files: File[]) => {
        const next = (uploadsRef.current ?? Promise.resolve())
            .then(() => uploadFiles(files))
            .catch(() => undefined);
        uploadsRef.current = next;
        void next.then(() => {
            if (uploadsRef.current === next) uploadsRef.current = null;
        });
        return next;
    }, [uploadFiles]);

    const armPastedText = useCallback((text: string) => {
        const label = labelFromPastedText(text, appState.t("chat.pastedContent"));
        const file = new File([text], `${label}.txt`, { type: "text/plain" });
        void startUpload([file]);
    }, [startUpload]);

    onArmTextRef.current = armPastedText;
    onFilesRef.current = (files) => { void startUpload(files); };

    const applySlashText = useCallback((command: string, opts?: { index?: number; trailingSpace?: boolean }) => {
        const trailingSpace = opts?.trailingSpace ?? false;
        if (opts?.index != null) setSlashIndex(opts.index);
        skipSlashIndexResetRef.current = !trailingSpace;
        suppressSlashOpenRef.current = trailingSpace;
        setSlashQuery(trailingSpace ? null : command);
        const editor = lexicalEditorRef.current;
        let placed = false;
        let unregister: (() => void) | undefined;
        const placeCaret = () => {
            if (placed) return;
            placed = true;
            unregister?.();
            unregister = undefined;
            editor?.update(() => {
                $getRoot().selectEnd();
                if (trailingSpace) {
                    const selection = $getSelection();
                    if ($isRangeSelection(selection)) {
                        selection.insertText(" ");
                    }
                }
            });
            skipSlashIndexResetRef.current = false;
        };
        if (editor) {
            unregister = editor.registerUpdateListener(() => {
                placeCaret();
            });
        }
        inputEditor.current?.setMarkdown("/" + command);
        window.setTimeout(placeCaret, 0);
        if (trailingSpace) {
            window.setTimeout(() => {
                suppressSlashOpenRef.current = false;
            }, 50);
        }
    }, []);

    const autocompleteSlash = useCallback(() => {
        if (slashMatches.length === 0) return;
        const query = (slashQuery ?? "").toLowerCase();
        const prefix = longestCommonPrefix(slashMatches.map((skill) => skill.command));
        if (slashMatches.length > 1 && prefix.toLowerCase().length > query.length) {
            applySlashText(prefix);
            return;
        }
        const selected = slashMatches.length === 1
            ? slashMatches[0]
            : (slashMatches[slashIndex] ?? slashMatches[0]);
        applySlashText(selected.command, {
            index: slashMatches.indexOf(selected),
            trailingSpace: true,
        });
    }, [applySlashText, slashIndex, slashMatches, slashQuery]);

    const send = async (msg?: string) => {
        if (sendingRef.current) return;
        const text = (msg ?? "").trim();
        // Skills start a turn of their own, so mid-run the text is just a message.
        const parsed = runningRef.current ? null : parseSlashSend(text);
        if (parsed) {
            const exact = skills.find((s) => s.command.toLowerCase() === parsed.token.toLowerCase());
            if (exact) {
                sendingRef.current = true;
                try {
                    await invokeSkill(exact.key, parsed.rest);
                    setChips([]);
                } finally {
                    sendingRef.current = false;
                }
                return;
            }
            if (!parsed.rest && slashQuery != null) {
                const pick = slashMatches.length === 1 ? slashMatches[0] : slashMatches[slashIndex];
                if (pick) {
                    sendingRef.current = true;
                    try {
                        await invokeSkill(pick.key);
                        setChips([]);
                    } finally {
                        sendingRef.current = false;
                    }
                    return;
                }
            }
        }
        const sendableChips = () => chipsRef.current.some(
            (c) => !c.uploading && c.documentId > 0
        );
        const stillUploading = chipsRef.current.some((c) => c.uploading);
        if (!text && !stillUploading && !sendableChips()) return;
        if (text.length > 5000) {
            appState.setModal({
                title: appState.t("chat.inputTooLongTitle"),
                body: appState.t("chat.inputTooLongBody"),
            });
            return;
        }
        sendingRef.current = true;
        blurComposerOnTouch();
        try {
            if (stillUploading) {
                // The upload appends the document's user_action to the conversation,
                // so it has to land before the text or the agent reads them out of
                // order. Hold the send rather than dropping it.
                setSendQueued(true);
                try {
                    await uploadsRef.current;
                } finally {
                    setSendQueued(false);
                }
            }
            // Chips only carry a document id once their upload resolved, and a
            // failed upload removes its chip, so settle this after the wait.
            const hasChips = sendableChips();
            if (!text && !hasChips) return;
            const armed = persistDocumentChips();
            if (!armed) return;
            const prompt = text || (hasChips ? appState.t("chat.attachedDocuments") : "");
            if (!prompt) return;
            await onSendMessage(prompt);
            inputEditor.current?.setMarkdown("");
            setChips([]);
        } finally {
            sendingRef.current = false;
        }
    };

    useEffect(() => {
        const enterKeyDown = (e: any) => {
            if (e.key === "Enter" && !e.shiftKey && e._lexicalHandled) {
                const msg = inputEditor.current?.getMarkdown();
                send(msg);
                return true;
            }
        };

        window.addEventListener("keydown", enterKeyDown);
        return () => {
            window.removeEventListener("keydown", enterKeyDown);
        };
    }, [inputEditor, chatState.conversation, slashQuery, slashMatches, slashIndex, skills, onSendMessage]);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (slashQuery == null) return;
            const root = composerRef.current;
            if (!root || !(e.target instanceof Node) || !root.contains(e.target)) return;

            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                setSlashQuery(null);
                return;
            }

            if (slashMatches.length === 0) return;

            if (e.key === "Tab") {
                e.preventDefault();
                e.stopPropagation();
                if (e.shiftKey) {
                    setSlashIndex((i) => Math.max(i - 1, 0));
                } else {
                    autocompleteSlash();
                }
                return;
            }
            if (e.key === "ArrowDown") {
                e.preventDefault();
                e.stopPropagation();
                setSlashIndex((i) => Math.min(i + 1, slashMatches.length - 1));
                return;
            }
            if (e.key === "ArrowUp") {
                e.preventDefault();
                e.stopPropagation();
                setSlashIndex((i) => Math.max(i - 1, 0));
            }
        };

        window.addEventListener("keydown", onKeyDown, true);
        return () => {
            window.removeEventListener("keydown", onKeyDown, true);
        };
    }, [slashQuery, slashMatches, slashIndex, autocompleteSlash]);

    // The composer overlays the messages; they reserve its height as bottom padding.
    useEffect(() => {
        const dock = dockRef.current;
        const root = chatRootRef.current;
        if (!dock || !root) return;
        let frame = 0;
        const observer = new ResizeObserver(() => {
            // Writing the height inside the callback resizes the message list in the same
            // frame and the browser reports a ResizeObserver loop.
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const next = `${dock.offsetHeight}px`;
                if (root.style.getPropertyValue("--composer-h") !== next) {
                    root.style.setProperty("--composer-h", next);
                }
            });
        });
        observer.observe(dock);
        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        const active = composerRef.current?.querySelector("[data-slash-active='true']");
        if (active instanceof HTMLElement) {
            active.scrollIntoView({ block: "nearest" });
        }
    }, [slashIndex, slashMatches]);



    const handleSend = useCallback(() => {
        const msg = inputEditor.current?.getMarkdown();

        send(msg);
    }, [inputEditor, chatState.conversation, onSendMessage]);

    const MkMessageClass = useCallback((x: ChatMessage) => {
        let classStr = "chat-message-body";
        if (x.role === "user") {
            classStr += " chat-message-user";
            if (x.is_pre_prompt) {
                classStr += " chat-message-preprompt";
            } else if (x.is_identity) {
                classStr += " chat-message-identity";
            }
        }
        return classStr;
    }, []);


    return (

        <>

            <div ref={chatRootRef} className="chat-column pt-nav flex flex-col justify-center min-h-full grow min-w-0 max-md:h-full max-md:min-h-0 md:min-w-[10rem] md:w-[20rem] md:max-w-[1024px]">

                <div ref={messagesScrollRef} className="chat-messages-scroller">
                <div key={appState.chat_refresh} className="chat-messages b flex flex-col w-full min-w-0 pt-3 justify-center md:px-1 z-auto">
                    {chatState.conversation?.map((con_block, i) => (

                        <div className={`chat-turn ${con_block.role == 'assistant' ? "chat-turn-assistant" : ""}`} key={i}>
                            {/* {
                                con_block.agent_id ? (
                                    <div className={`w-full h-[15.5px] bg-black/10 opacity-40 px-1 flex flex-row justify-baseline`}>
                                        <small className="leading-[0.7] text-1 text-black/30 pt-[2px] grow"><b>{appState.agents?.filter(x => x.id == con_block.agent_id)?.[0]?.name}</b></small>
                                    </div>
                                )
                                    :
                                    null
                            } */}
                            <div className="h-[0.5rem]" />
                            {
                                (() => {
                                    // A turn's text refers by label to citations its validate_citation results registered.
                                    const blockCitations = buildCitationRegistry(con_block.content);

                                    return con_block.content.map((msg, j) => {
                                        return (
                                            <div className="chat-row" id={`message_${msg.id}`} key={j}>
                                                {msg.role === 'user' && !msg.is_pre_prompt && !msg.is_identity && con_block.role != 'assistant' && (
                                                    <div className="absolute z-40 flex justify-center gap-1">
                                                        <button
                                                            className="ghost-action"
                                                            onClick={() => onSendMessage("Replay", false, msg.id)}
                                                        >
                                                            <Replay size={16} />
                                                        </button>
                                                        <CopySnapshotButton />
                                                    </div>
                                                )}
                                                <MessageContent
                                                    key={j}
                                                    msg={msg as ChatMessage}
                                                    MkMessageClass={MkMessageClass}
                                                    citations={blockCitations}
                                                />

                                            </div>
                                        );
                                    })
                                })()
                            }

                        </div >
                    ))
                    }

                    {(running || chatState.state !== "idle") && <StatusIndicator cb={running ? onCancel : undefined} />}


                </div >
                </div>

                {/* Input Field */}

                <div className="composer-dock">
                    {/* <button onClick={() => { chatState.state == 'idle' ? chatState.setState("streaming") : chatState.setState("idle")}} >bla</button> */}
                    <div ref={dockRef} className="composer-dock-inner">
                        <div className='flex flex-col items-center w-full'>

                            <div onClick={() => scrollRef?.current?.scrollTo({ top: 0, behavior: 'smooth' })} className={`scroll-fab ${scrollRefIsAtBottom ? "scroll-fab-hidden" : ""}`}>

                                <ArrowDown size={20} />
                            </div>
                            <div className="composer" onFocus={onComposerFocus}>
                                    <div
                                        ref={composerRef}
                                        className='h-full justify-center w-full relative'
                                        onDragOver={(e) => {
                                            if (e.dataTransfer?.types?.includes("Files")) {
                                                e.preventDefault();
                                            }
                                        }}
                                        onDrop={(e) => {
                                            const files = Array.from(e.dataTransfer?.files || []);
                                            if (files.length) {
                                                e.preventDefault();
                                                void startUpload(files);
                                            }
                                        }}
                                    >
                                        {slashQuery != null && !running && (
                                            <div
                                                role="listbox"
                                                className="slash-menu"
                                            >
                                                {slashMatches.length === 0 ? (
                                                    <div className="slash-empty">{appState.t("chat.skill.paletteEmpty")}</div>
                                                ) : slashMatches.map((skill, i) => (
                                                    <button
                                                        key={skill.key}
                                                        type="button"
                                                        role="option"
                                                        aria-selected={i === slashIndex}
                                                        data-slash-active={i === slashIndex ? "true" : undefined}
                                                        className={`slash-option ${i === slashIndex ? "slash-option-active" : ""}`}
                                                        onMouseEnter={() => setSlashIndex(i)}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            invokeSkill(skill.key);
                                                        }}
                                                    >
                                                        /{skill.command}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        {chips.length > 0 && (
                                            <div className="flex flex-wrap gap-1 p-1 pb-0">
                                                {chips.map((chip) => (
                                                    <div
                                                        key={chip.id}
                                                        className="attachment"
                                                    >
                                                        <button
                                                            type="button"
                                                            className="attachment-label"
                                                            title={chip.label}
                                                            onClick={() => {
                                                                if (chip.documentId > 0) {
                                                                    chatState.setSelectedObjectKey({ type: "document", id: chip.documentId }, false);
                                                                }
                                                            }}
                                                        >
                                                            {chip.uploading ? appState.t("chat.uploadingFile") : chip.label}
                                                            <span className="attachment-meta">{chip.sizeHint}</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            title={appState.t("chat.dismissAttachment")}
                                                            className="attachment-dismiss"
                                                            onClick={() => setChips((prev) => prev.filter((c) => c.id !== chip.id))}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ))}
                                                {sendQueued && (
                                                    <div className="attachment-note">
                                                        {appState.t("chat.sendQueuedForUpload")}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div className='flex w-full'>
                                            <div className="composer-editor">
                                                <ForwardRefEditor
                                                    autoFocus={true}
                                                    plugins={[headingsPlugin(), listsPlugin(), quotePlugin(), thematicBreakPlugin(), tablePlugin(), codeBlockPlugin({ defaultCodeBlockLanguage: '' }), codeMirrorPlugin({ codeBlockLanguages: { '': 'Plain Text' } }), markdownShortcutPlugin(), EnterHandlerPlugin({ lexicalEditorRef }), PasteHandlerPlugin({ onArmText: onArmTextRef, onFiles: onFilesRef })]}
                                                    markdown={""}
                                                    suppressHtmlProcessing={true}
                                                    contentEditableClassName="composer-content"
                                                    ref={inputEditor as React.RefObject<MDXEditorMethods>}
                                                    onChange={(md) => {
                                                        const source = lexicalEditorRef.current
                                                            ? lexicalEditorRef.current.getEditorState().read(() => $getRoot().getTextContent())
                                                            : md;
                                                        const query = parseSlashQuery(source);
                                                        if (suppressSlashOpenRef.current && query != null) {
                                                            setSlashQuery(null);
                                                            return;
                                                        }
                                                        if (query != null) {
                                                            setSlashQuery(query);
                                                            if (skipSlashIndexResetRef.current) {
                                                                skipSlashIndexResetRef.current = false;
                                                            } else {
                                                                setSlashIndex(0);
                                                            }
                                                        } else {
                                                            suppressSlashOpenRef.current = false;
                                                            setSlashQuery(null);
                                                        }
                                                    }}
                                                />
                                            </div>

                                        </div>
                                    </div>
                                    <div className='flex flex-row items-center justify-center mr-[.25rem] self-stretch'>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="application/pdf,.pdf"
                                            className="hidden"
                                            onChange={(e) => {
                                                const files = Array.from(e.target.files || []);
                                                e.target.value = "";
                                                if (files.length) void startUpload(files);
                                            }}
                                        />
                                        <button
                                            type="button"
                                            title={uploadDisabled ? appState.t("chat.uploadNeedsSingleTag") : appState.t("chat.attachFile")}
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={chatState.state === "awaiting_backend" || uploadDisabled}
                                            className="btn btn-ghost"
                                        >
                                            <div className="composer-icon">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </div>
                                        </button>
                                        {running && (
                                            <button
                                                type="button"
                                                title={appState.t("chat.stop")}
                                                onClick={onCancel}
                                                className="btn btn-ghost"
                                            >
                                                <div className="composer-icon" aria-hidden="true">
                                                    <span className="composer-stop" />
                                                </div>
                                            </button>
                                        )}
                                        <button
                                            id="Action handler button"
                                            title={running ? appState.t("chat.queueMessage") : undefined}
                                            onClick={() => {
                                                handleSend();
                                            }}
                                            disabled={chatState.state === "awaiting_backend"}
                                            className="btn btn-ghost"
                                        >
                                            <div className="composer-icon">
                                                {">"}
                                            </div>
                                        </button>
                                    </div>
                                </div>
                        </div>

                    </div>

                </div>

            </div >



        </>

    );


});
